import { cn } from '@/utilities/ui'
import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import Link from 'next/link'
import * as React from 'react'

const buttonVariants = cva('', {
  variants: {
    variant: {
      // Figma-accurate variants
      primary: 'btn-primary',
      secondary: 'btn-secondary',
      // Utility variants
      default: 'btn-primary',
      destructive:
        'btn-base rounded-md bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90',
      outline:
        'btn-base rounded-md border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground',
      ghost: 'btn-base hover:bg-accent hover:text-accent-foreground',
      link: 'btn-base text-primary underline-offset-4 hover:underline',
    },
    size: {
      clear: '',
      default: '',
      sm: 'btn-sm',
      lg: 'px-8 py-5 text-[20px]',
      icon: 'size-10',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
})

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  href?: string
  variant?:
    | 'primary'
    | 'secondary'
    | 'default'
    | 'destructive'
    | 'outline'
    | 'ghost'
    | 'link'
    | null
  size?: 'clear' | 'default' | 'sm' | 'lg' | 'icon' | null
}

const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  ({ className, variant, size, asChild = false, href, ...props }, ref) => {
    if (asChild) {
      return (
        <Slot
          data-slot="button"
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref as any}
          {...props}
        />
      )
    }

    if (href) {
      return (
        <Link
          href={href}
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref as any}
          {...(props as any)}
        />
      )
    }

    return (
      <button
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref as any}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
